// Centralized WSR Export Service
// This service handles WSR exports from the top navbar

interface WSRExportData {
  id?: number;
  client_id: number;
  week_ending: string;
  reporting_period: string;
  reporting_date: string;
  project_sponsor: string;
  prepared_by: string;
  phone: string;
  email: string;
  report_summary: string;
  conclusions_recommendations: string;
  status: string;
  accomplished: any[];
  targets: any[];
  risks: any[];
  metrics: any[];
}

class WSRExportService {
  private static instance: WSRExportService;

  static getInstance(): WSRExportService {
    if (!WSRExportService.instance) {
      WSRExportService.instance = new WSRExportService();
    }
    return WSRExportService.instance;
  }

  // Export WSR as Markdown
  async exportAsMarkdown(wsrData: WSRExportData): Promise<void> {
    const markdown = this.generateMarkdown(wsrData);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `WSR_WE_${wsrData.week_ending.replace(/-/g, '')}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Export WSR as PDF (using browser print)
  async exportAsPDF(): Promise<void> {
    window.print();
  }

  // Export WSR as JSON
  async exportAsJSON(wsrData: WSRExportData): Promise<void> {
    const json = JSON.stringify(wsrData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `WSR_WE_${wsrData.week_ending.replace(/-/g, '')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Generate email with WSR content
  async emailWSR(wsrData: WSRExportData): Promise<void> {
    const subject = `Weekly Status Report - ${wsrData.reporting_period}`;
    const body = this.generateEmailBody(wsrData);

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  }

  // Generate shareable link
  generateShareableLink(wsrId: number): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/wsr/view/${wsrId}`;
  }

  // Copy WSR content to clipboard
  async copyToClipboard(wsrData: WSRExportData): Promise<void> {
    const markdown = this.generateMarkdown(wsrData);
    await navigator.clipboard.writeText(markdown);
  }

  private generateMarkdown(wsrData: WSRExportData): string {
    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    return `
| Project Sponsor | ${wsrData.project_sponsor} |
| :---- | :---- |
| **Reporting Period** | ${wsrData.reporting_period} |
| **Reporting Date** | ${formatDate(wsrData.reporting_date)} |
| **Prepared By** | ${wsrData.prepared_by} | ${wsrData.phone} | ${wsrData.email} |

## REPORT SUMMARY

${wsrData.report_summary}

## WORK ACCOMPLISHED PREVIOUS WEEK

${wsrData.accomplished.length > 0 ?
        wsrData.accomplished.map(item =>
          `| ${item.task || item.description} | ${item.notes || ''} |`
        ).join('\n') :
        '| No work items recorded | |'
      }

## NEXT WEEK'S TARGETS

${wsrData.targets.length > 0 ?
        wsrData.targets.map(item =>
          `| ${item.task || item.description} | ${item.notes || ''} |`
        ).join('\n') :
        '| No targets set | |'
      }

## RISKS & BOTTLENECKS

${wsrData.risks.length > 0 ?
        wsrData.risks.map(item =>
          `| ${item.risk || item.description} | ${item.notes || ''} |`
        ).join('\n') :
        '| No risks identified | |'
      }

${wsrData.metrics.length > 0 ? `
## KEY PERFORMANCE METRICS

| Metric | Current Value | Target Value | Status | Trend |
| :---- | :---- | :---- | :---- | :---- |
${wsrData.metrics.map(metric =>
        `| ${metric.metric_name} | ${metric.current_value} | ${metric.target_value} | ${metric.status} | ${metric.trend} |`
      ).join('\n')}
` : ''}

## CONCLUSIONS/RECOMMENDATIONS

${wsrData.conclusions_recommendations}

---
*Generated on ${new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}*
`.trim();
  }

  private generateEmailBody(wsrData: WSRExportData): string {
    return `
Weekly Status Report - ${wsrData.reporting_period}

Project Sponsor: ${wsrData.project_sponsor}
Prepared By: ${wsrData.prepared_by}
Contact: ${wsrData.phone} | ${wsrData.email}

REPORT SUMMARY:
${wsrData.report_summary}

WORK ACCOMPLISHED:
${wsrData.accomplished.map((item, index) =>
      `${index + 1}. ${item.task || item.description}${item.notes ? ` - ${item.notes}` : ''}`
    ).join('\n')}

NEXT WEEK TARGETS:
${wsrData.targets.map((item, index) =>
      `${index + 1}. ${item.task || item.description}${item.notes ? ` - ${item.notes}` : ''}`
    ).join('\n')}

RISKS & BOTTLENECKS:
${wsrData.risks.map((item, index) =>
      `${index + 1}. ${item.risk || item.description}${item.notes ? ` - ${item.notes}` : ''}`
    ).join('\n')}

${wsrData.metrics.length > 0 ? `
KEY METRICS:
${wsrData.metrics.map(metric =>
      `• ${metric.metric_name}: ${metric.current_value} (Target: ${metric.target_value}) - ${metric.status}`
    ).join('\n')}
` : ''}

CONCLUSIONS & RECOMMENDATIONS:
${wsrData.conclusions_recommendations}

Best regards,
${wsrData.prepared_by}
`.trim();
  }
}

export default WSRExportService;